<template>
  <div class="book-cover-stack">
    <template v-for="cover in covers" :key="cover.slug">
      <div
        v-if="cover.useFallback"
        class="book-cover-stack__cover book-cover-stack__cover--fallback"
      >
        <BookFallbackCover :title="cover.title" :subtitle="cover.subtitle" />
      </div>
      <img
        v-else
        :src="cover.url"
        :alt="cover.title"
        class="book-cover-stack__cover"
        @error="onCoverError(cover.slug)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import BookFallbackCover from '@/app/components/book/card/BookFallbackCover.vue'
import type { InterestBook } from '@/helpers/billingSourceBook'
import {
  getBookCoverStackSlugs,
  getBookCoverUrl,
  titleFromBookSlug,
} from '@/helpers/bookCoverStack'

const props = defineProps<{
  book: InterestBook
}>()

const coverErrors = ref<Set<string>>(new Set())

const covers = computed(() => {
  return getBookCoverStackSlugs(props.book).map((slug) => {
    const isSource = slug === props.book.url_slug
    return {
      slug,
      title: isSource ? props.book.title : titleFromBookSlug(slug),
      subtitle: isSource ? props.book.author || '' : '',
      url:
        isSource && props.book.cover_image
          ? props.book.cover_image
          : getBookCoverUrl(slug),
      useFallback:
        coverErrors.value.has(slug) || (isSource && !props.book.cover_image),
    }
  })
})

function onCoverError(slug: string): void {
  coverErrors.value = new Set([...coverErrors.value, slug])
}
</script>

<style lang="scss" scoped>
.book-cover-stack {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;
  width: 100%;
  min-height: 160px;
  padding-block: 4px;

  &__cover {
    // Covers never shrink, so the stack sizes itself from this width rather
    // than from its container. Parents can set `--book-cover-width` to fit
    // the stack into a narrower box.
    --base-book-cover-width: var(--book-cover-width, 120px);
    position: relative;
    flex-shrink: 0;
    display: block;
    height: auto;
    margin-inline: -18px;
    border: 2px solid rgba($c-black-2, 0.1);
    border-radius: 12px;
    box-shadow: $shadow;
    background: $c-white;

    @include book-cover-fan(14, 3);

    &--fallback {
      overflow: hidden;
      background: unset;
      aspect-ratio: 120 / 160;

      :deep(.fallback-cover) {
        width: 100%;
        height: 100%;
        aspect-ratio: unset;
        border-radius: inherit;
      }
    }
  }
}
</style>
